// src/pages/Suppliers/Suppliers.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, onSnapshot, query, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    searchContainer: {
        marginBottom: '24px'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    balancePositive: {
        color: '#059669',
        fontWeight: '600'
    },
    balanceNegative: {
        color: '#dc2626',
        fontWeight: '600'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    badge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        marginLeft: '8px'
    },
    confectioneryBadge: {
        backgroundColor: '#dbeafe',
        color: '#2563eb'
    }
};

function AddSupplierModal({ onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        taxOffice: '',
        taxNumber: '',
        currency: 'TRY'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Yeni Tedarikçi Ekle</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: 'pointer',
                            color: '#94a3b8'
                        }}
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Tedarikçi Adı *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            style={styles.input}
                            placeholder="Tedarikçi adı girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>E-posta</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="E-posta adresi girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Telefon</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Telefon numarası girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Adres</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Adres girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Vergi Dairesi</label>
                        <input
                            type="text"
                            name="taxOffice"
                            value={formData.taxOffice}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Vergi dairesi girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Vergi Numarası</label>
                        <input
                            type="text"
                            name="taxNumber"
                            value={formData.taxNumber}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Vergi numarası girin"
                        />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Bakiye Döviz Türü *</label>
                        <select
                            name="currency"
                            value={formData.currency}
                            onChange={handleChange}
                            required
                            style={styles.select}
                        >
                            <option value="TRY">TL (₺)</option>
                            <option value="USD">USD ($)</option>
                        </select>
                    </div>
                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                ...styles.button,
                                background: '#f1f5f9',
                                color: '#374151',
                                padding: '10px 20px'
                            }}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            style={{
                                ...styles.button,
                                padding: '10px 20px'
                            }}
                        >
                            Ekle
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Suppliers() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [suppliers, setSuppliers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [supplierCosts, setSupplierCosts] = useState({});

    // Load suppliers and their confectionery costs
    useEffect(() => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;
        const suppliersQuery = query(collection(db, 'companies', companyId, 'suppliers'));

        const unsubscribe = onSnapshot(suppliersQuery, async (snapshot) => {
            const suppliersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setSuppliers(suppliersData);

            // Load confectionery costs for each supplier
            const costs = {};
            for (const supplier of suppliersData) {
                try {
                    // Get confectionery orders where location matches supplier name
                    const confectioneryOrdersQuery = query(
                        collection(db, 'companies', companyId, 'confectioneryOrders'),
                        where('location', '==', supplier.name)
                    );

                    const confectioneryOrdersSnapshot = await getDocs(confectioneryOrdersQuery);
                    let totalCost = 0;

                    confectioneryOrdersSnapshot.forEach(doc => {
                        const order = doc.data();
                        if (order.productionCost && order.productionCost > 0) {
                            totalCost += order.productionCost;
                        }
                    });

                    costs[supplier.id] = totalCost;
                } catch (error) {
                    console.error(`Error loading costs for supplier ${supplier.id}:`, error);
                }
            }

            setSupplierCosts(costs);
        });

        return () => unsubscribe();
    }, [userData]);

    const handleAddSupplier = async (supplierData) => {
        try {
            await addDoc(collection(db, 'companies', userData.companyId, 'suppliers'), {
                ...supplierData,
                balance: 0,
                createdAt: new Date()
            });
            setShowAddModal(false);
        } catch (error) {
            console.error("Error adding supplier:", error);
            alert('Tedarikçi eklenirken bir hata oluştu: ' + error.message);
        }
    };

    const filteredSuppliers = suppliers.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Tedarikçiler</h1>
                    <p style={styles.subtitle}>Tedarikçi listesi ve bakiyeleri</p>
                </div>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => setShowAddModal(true)}
                        style={styles.button}
                    >
                        + Yeni Tedarikçi
                    </button>
                </div>
            </div>

            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Tedarikçi ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                {filteredSuppliers.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📋</div>
                        <h3 style={styles.emptyText}>Tedarikçi Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            {searchTerm ? 'Arama kriterlerinize uygun tedarikçi bulunamadı.' : 'Henüz hiç tedarikçi eklenmemiş.'}
                        </p>
                        <button
                            onClick={() => setShowAddModal(true)}
                            style={{ ...styles.button, marginTop: '20px' }}
                        >
                            İlk Tedarikçinizi Ekleyin
                        </button>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Tedarikçi Adı</th>
                                    <th style={styles.th}>E-posta</th>
                                    <th style={styles.th}>Bakiye</th>
                                    <th style={styles.th}>Konfeksiyon Maliyeti</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSuppliers.map(supplier => (
                                    <tr
                                        key={supplier.id}
                                        style={{ ...styles.trHover, ...(hoveredRow === supplier.id ? styles.trHoverActive : {}) }}
                                        onMouseEnter={() => setHoveredRow(supplier.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/tedarikciler/${supplier.id}`)}
                                    >
                                        <td style={styles.td}>
                                            {supplier.name}
                                            {supplierCosts[supplier.id] > 0 && (
                                                <span style={{ ...styles.badge, ...styles.confectioneryBadge }}>
                                                    Konfeksiyon
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>{supplier.email || '-'}</td>
                                        <td style={{ ...styles.td, ...(supplier.balance >= 0 ? styles.balancePositive : styles.balanceNegative) }}>
                                            {(supplier.balance || 0).toFixed(2)} {supplier.currency === 'USD' ? '$' : '₺'}
                                        </td>
                                        <td style={{ ...styles.td, color: supplierCosts[supplier.id] > 0 ? '#2563eb' : '#94a3b8', fontWeight: supplierCosts[supplier.id] > 0 ? '600' : 'normal' }}>
                                            {supplierCosts[supplier.id] > 0
                                                ? `${supplierCosts[supplier.id].toFixed(2)} ₺`
                                                : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>



            {showAddModal && <AddSupplierModal onClose={() => setShowAddModal(false)} onSave={handleAddSupplier} />}
        </div>
    );
}

export default Suppliers;